import { createElement, forwardRef, useMemo } from "react";
import { parseURL, resolveURL, withoutTrailingSlash } from "ufo";
import { useIsSSR } from "../hooks/useIsSSR.js";
import { useRouter } from "../hooks/useRouter.js";
import { addTrailingSlash, getLinkHandlerMode, isSameOrigin } from "../utils.js";
import { useRoute } from "../hooks/useRoute.js";
import { useRouterState } from "../hooks/useRouterState.js";
export function useLinkProps(props, ref) {
    if (env.admin) {
        return {
            ref: ref,
            ...props,
            href: props.href ?? undefined,
            onClick: (e) => {
                props.onClick?.(e);
                e.preventDefault();
            },
        };
    }
    const router = useRouter();
    const state = useLinkState(props.href ?? "");
    return {
        ref: ref,
        "data-active": state.active ?? undefined,
        "data-child-active": state.childActive ?? undefined,
        "data-pending": state.pending ?? undefined,
        ...props,
        href: addTrailingSlash(props.href),
        onMouseEnter: (e) => {
            if (props.onMouseEnter) {
                props.onMouseEnter(e);
            }
            const mode = getLinkHandlerMode(e, props.href);
            if (mode.mode === "navigate") {
                router.preload(props.href);
            }
        },
        onClick: (e) => {
            props.onClick?.(e);
            router.handleClickEvent(e, props.href ?? undefined, props.preferBack);
        },
    };
}
export const Link = forwardRef(({ preferBack, as, ...props }, ref) => {
    const Comp = as || "a";
    const finalProps = useLinkProps(props, ref);
    return createElement(Comp, finalProps);
});
// @ts-ignore
Link.displayName = "Link";
/**
 * Provides information about the given href parameter, such as whether it is active, whether a child page is active, or whether it is loading.
 *
 * This is used internally by the `<Link />` component for the `data-active`, `data-child-active` and `data-pending` attributes, but is exposed for use in custom components.
 */
export function useLinkState(href) {
    const route = useRoute();
    const pendingRoute = useRouterState((r) => r.pendingRoute);
    const isSSR = useIsSSR();
    const state = useMemo(() => {
        // If we're in SSR mode, do a quick and dirty check, which relies on the href being relative.
        if (isSSR) {
            if (href === route.pathname)
                return {
                    active: true,
                };
            return {};
        }
        // Bail out for external links
        if (!href || !isSameOrigin(href)) {
            return {};
        }
        const hrefUrl = parseURL(resolveURL(href));
        const active = route ? compareUri(route, hrefUrl) : undefined;
        const childActive = !active && route ? isSubpath(hrefUrl.pathname, route.pathname) || undefined : undefined;
        const pending = pendingRoute ? compareUri(pendingRoute, hrefUrl) || undefined : undefined;
        return {
            active,
            childActive,
            pending,
        };
    }, [isSSR, href, route, pendingRoute]);
    return state;
}
/**
 * Returns the active-ness state for the given link.
 * path = the path matches, but not the query/hash
 * query = the path and query matches, but not the hash
 * exact = the path, query and hash all match exactly
 */
function compareUri(route, href) {
    // If they're directly equal, then jump straight to a 'hash' match
    if (withoutTrailingSlash(route.pathname) === withoutTrailingSlash(href.pathname)) {
        if (route.search.replace(/^\?/, "") === href.search.replace(/^\?/, "")) {
            if (route.hash === href.hash) {
                return "exact";
            }
            return "query";
        }
        return "path";
    }
}
function isSubpath(parentPath, childPath) {
    if (parentPath === "/" || childPath === "/") {
        return false;
    }
    if (withoutTrailingSlash(parentPath) === withoutTrailingSlash(childPath)) {
        return false;
    }
    if (childPath.startsWith(parentPath)) {
        return true;
    }
}
